import { AuthorSchema, BaseVersionSchema, HlcSchema, OpIdSchema } from '@dl/shared';
import { z } from 'zod';

/**
 * An op record is the unit of storage in the substrate (02-architecture.md
 * §4.1). Every fact mutation and every reveal is one of these. The op log
 * is the source of truth; current-value tables and projection caches are
 * rebuildable views over it.
 *
 * The payload shape varies by op kind (fact.create / fact.edit / reveal /
 * unreveal / …). The schema factory below lets callers parameterise the
 * payload while keeping the envelope fixed.
 */
export const OpRecordSchema = <Payload extends z.ZodTypeAny>(payload: Payload) =>
  z
    .object({
      /** Server-assigned id. Stable. */
      id: OpIdSchema,
      /** The entity (or fact) the op is about. Stable identifier. */
      subject: z.string().min(1),
      /** The predicate name (registry-resolved on the read side). */
      predicate: z.string().min(1),
      /** Op-kind-specific payload. */
      payload,
      /**
       * The op id this op was based on, or `null` for the first op on a
       * (subject, predicate) chain. Validated by the server on accept.
       */
      base_version: BaseVersionSchema,
      /** Server HLC at acceptance. Defines total order. */
      hlc: HlcSchema,
      /** Who wrote this op. */
      author: AuthorSchema,
      /** Wall-clock at write time (ISO 8601). Informational only. */
      created_at_real: z.iso.datetime(),
    })
    .strict();

export type OpRecord<Payload = unknown> = z.input<ReturnType<typeof OpRecordSchema<z.ZodType<Payload>>>>;
